import React from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import SaveIcon from '@mui/icons-material/Save';
import { IIrpAddEditRequest } from '../../../../model/scp/irp/request/irp-add-edit-request';
import { IIrpItemResponse } from '../../../../model/scp/irp/response/irp-item-response';
import { Box, Button, Grid, IconButton, LinearProgress, Tooltip } from '@mui/material';
import DefaultDrawer from '../../../../components/default-drawer';
import { DefaultDialogProps } from '../../../../components/default-dialog';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import IrpItemForm from './irp-item-form';
import { dataGridLocaleConfig } from '../../../../config/data-grid-locale';
import { useIrpItemController } from './controllers/irp-item-controller';

export interface IItemIrpDataTableProps {
    setItemsIrpJson: (value: string) => void,
    irpRequest: IIrpAddEditRequest,
    irpId?: string | undefined,
    itemsIrpJson?: string
};

export default function IrpItemDataTable({ props }: { props: IItemIrpDataTableProps }) {
    const controller = useIrpItemController(
        props.irpId,
        props.irpRequest.modalityId,
        props.setItemsIrpJson,
        props.itemsIrpJson
    );

    const {
        itemIrp,
        openDrawer,
        setOpenDrawer,
        materialService,
        categoryItemPca,
        judgementCriteria,
        titleDrawer,
        irpItems,
        irpId,
        loadMoreData,
        isRetification,
        page,
        hasNextPage,
        itemsPageSize,
        handleOnChange,
        handleCurrencyChange,
        handlePageChange,
        addItem,
        editItem,
        saveItem,
        deleteItem,
        showDialog,
        showMessage,
    } = controller;

    const currencyFormatter = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 4, maximumFractionDigits: 4
    });

    const columns: GridColDef<IIrpItemResponse>[] = [
        {
            field: 'number',
            headerName: 'Nº',
            minWidth: 80, sortable: false,
            disableColumnMenu: true,
            align: 'right',
            headerAlign: 'right'
        },
        {
            field: 'typeItemDescription',
            headerName: 'Tipo',
            flex: 1,
            minWidth: 120, sortable: false,
            disableColumnMenu: true,
            valueFormatter: ({ value }) => {
                const normalized = typeof value === 'string' ? value.trim().toUpperCase() : value;
                if (normalized === 'M') return 'MATERIAL';
                if (normalized === 'S') return 'SERVIÇO';
                return value;
            }
        },
        {
            field: 'description',
            headerName: 'Descrição',
            flex: 1,
            minWidth: 200, sortable: false,
            disableColumnMenu: true
        },
        {
            field: 'unitSupply',
            headerName: 'Unidade de Fornecimento',
            flex: 1,
            minWidth: 180,
            sortable: false,
            disableColumnMenu: true
        },
        {
            field: 'unitValueEstimated',
            headerName: 'Valor Estimado Unitário',
            flex: 1,
            minWidth: 190, sortable: false,
            disableColumnMenu: true,
            align: 'right',
            headerAlign: 'right',
            valueFormatter: ({ value }) => currencyFormatter.format(Number(value)),
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            width: 100,
            renderCell: (params) => {
                return [
                    <Tooltip title="Editar" key="edit">
                        <IconButton aria-label="Editar" onClick={() => {
                            editItem(params.row.number);
                        }}>
                            <EditIcon />
                        </IconButton>
                    </Tooltip>,
                    <Tooltip title="Excluir" key="delete">
                        <IconButton aria-label="Excluir" onClick={() => {
                            if (irpId === undefined) {
                                showDialog({
                                    open: true,
                                    message: "Deseja realmente excluir o item?",
                                    onYesClick: () => {
                                        deleteItem(params.row.number);
                                    }
                                } as DefaultDialogProps);
                            } else {
                                showDialog({
                                    title: 'Pergunta', message: 'Deseja realmente excluir o item? Caso deseje excluir preencha a justificativa abaixo:', onYesClick: (justification: string) => {
                                        if (justification !== undefined && justification.length > 0) {
                                            deleteItem(params.row.number, justification);
                                        } else {
                                            showMessage({ message: 'Informe a justificativa para excluir o item', type: 'warning', open: true });
                                        }
                                    }, inputProps: {
                                        rows: 4
                                    }
                                } as DefaultDialogProps);
                            }
                        }}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>,
                ];
            }
        }
    ];

    // O endpoint de itens da IRP não retorna total de registros: quando a IRP já
    // existe, a paginação é feita no servidor buscando uma página de cada vez
    // (50 itens por requisição); no rascunho local (antes de salvar) a paginação
    // continua no navegador, sobre os itens já carregados em memória.
    const commonInitialState = {
        columns: { columnVisibilityModel: { id: false } },
        sorting: { sortModel: [{ field: 'number', sort: 'asc' as const }] },
    };

    const paginationProps = irpId !== undefined
        ? {
            paginationMode: 'server' as const,
            paginationModel: { page, pageSize: itemsPageSize },
            onPaginationModelChange: (model: { page: number }) => handlePageChange(model.page),
            rowCount: hasNextPage ? (page + 1) * itemsPageSize + 1 : page * itemsPageSize + irpItems.length,
            pageSizeOptions: [itemsPageSize],
            initialState: commonInitialState,
        }
        : {
            pageSizeOptions: [10, 25, 50],
            initialState: {
                ...commonInitialState,
                pagination: { paginationModel: { pageSize: 50, page: 0 } },
            },
        };

    const formItem = () => {
        return (
            <React.Fragment>
                <DefaultDrawer
                    title={titleDrawer}
                    isOpen={openDrawer}
                    actions={() => {
                        return (
                            <Button variant="contained" onClick={saveItem} endIcon={<SaveIcon />}>
                                Salvar
                            </Button>
                        )
                    }}
                    onClose={() => {
                        setOpenDrawer(false);
                    }}
                >
                    <IrpItemForm props={{
                        itemIrp: itemIrp,
                        materialService,
                        categoryItemPca,
                        judgementCriteria,
                        isRetification,
                        handleOnChange,
                        handleCurrencyChange
                    }} />
                </DefaultDrawer>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            {formItem()}
            <Box sx={{ textAlign: 'right' }}>
                <Grid container spacing={2} sx={{ mt: '10px', mb: '10px', pr: '10px', justifyContent: 'flex-end' }}>
                    <Grid item xs="auto">
                        <Button variant="contained" component="label" size="large" endIcon={<PlaylistAddIcon />}
                            onClick={() => {
                                if (props.irpRequest.modalityId !== undefined && props.irpRequest.modalityId !== null && props.irpRequest.modalityId !== 0) {
                                    addItem();
                                } else {
                                    showMessage({ message: 'Antes de adicionar um item é necessário selecionar a modalidade', type: 'error', open: true });
                                }
                            }}
                        >
                            Adicionar
                        </Button>
                    </Grid>
                </Grid>
            </Box>
            <DataGrid
                sx={{ width: '100%', minWidth: 0 }}
                columns={columns}
                disableRowSelectionOnClick
                components={{
                    LoadingOverlay: LinearProgress,
                }}
                loading={loadMoreData}
                {...paginationProps}
                rows={irpItems}
                getRowId={(row: any) => row.number}
                localeText={dataGridLocaleConfig}
            />
        </React.Fragment>
    );
}
